import Shaka from 'shaka-player/dist/shaka-player.ui'
import UAParser from 'ua-parser-js'
import NekoPlayer from "./neko_player";

class MultiViewPointsMenu extends Shaka.ui.Element {
  constructor(parent, controls, playSettings, drm, src, player) {
    super(parent, controls);

    this._controlsContainer = this.controls.getControlsContainer();
    this._video = this.controls.getVideo();
    this._playSettings = playSettings;
    this._drm = drm;
    this._userAgent = UAParser(navigator.userAgent)
    this._streamingProtocol = this._getStreamingProtocol(src);

    // 視点変更ボタンとそれをオーバーフローした際に表示されるメニューを追加
    this._addOverflowMenuButton();
    this._addOverflowMenu();
    // 「DRM が有効の場合」あるいは「WebKit を使用したブラウザが MPEG-DASH を再生する場合」はサブビューを無効
    if (!this._drm && !(this._userAgent.engine.name === 'WebKit' && this._streamingProtocol === 'dash')) {
      this._addSubViewContainer();
      // NOTE: 現状サブビューは 16ms (60fps) での更新となっている
      this._updateInterval = setInterval(() => {
        if (!this._isPausing) this._updateSubViews(this.currentViewPoint), 16
      });
    }

    this._videoElement = Array.prototype.find.call(this.controls.getVideoContainer().children, (child) => {
      return child.tagName === 'VIDEO';
    });
    this._isPausing = true;
    // NOTE: viewPoint は undefined: 全体, 0: 左上, 1: 右上, 2:左下, 3: 右下
    this.currentViewPoint = undefined;

    // listen events
    this.eventManager.listen(this._overflowMenu, 'touchstart', (e) => {
      e.stopPropagation();
      this.controls.setLastTouchEventTime(Date.now);
    })

    this.eventManager.listen(this._overflowMenuButton, 'click', () => {
      this._onOverflowMenuButtonClick();
    });

    this.eventManager.listen(this._video, 'play', () => {
      this._isPausing = false;
    });

    this.eventManager.listen(this._video, 'pause', () => {
      this._isPausing = true;
    });

    this.eventManager.listen(window, 'resize', () => {
      this._changeViewPoint(this.currentViewPoint);
    });

    this.eventManager.listen(document, 'fullscreenchange', () => {
      this._changeViewPoint(this.currentViewPoint);
    });

    player.addEvent(NekoPlayer.Events.DidEnterFullscreen, () => {
      this._changeViewPoint(this.currentViewPoint);
    })

    player.addEvent(NekoPlayer.Events.DidExitFullscreen, () => {
      this._changeViewPoint(this.currentViewPoint);
    })

    window.addEventListener('stopUpdateSubViews', () => {
      window.clearInterval(this._updateInterval)
    })
  }

  /**
   * overflowMenuButton を追加
   * @private
   */
  _addOverflowMenuButton() {
    this._overflowMenuButton = document.createElement('button');
    this._overflowMenuButton.id = 'multi-view-points-menu-button';
    // NOTE: class で material-icons mdi アイコン名 を指定するとそのアイコンが表示される
    // https://materialdesignicons.com
    this._overflowMenuButton.classList.add('shaka-overflow-menu-button', 'shaka-no-propagation')
    this._overflowMenuButton.classList.add('material-icons', 'mdi', 'mdi-binoculars');
    this.parent.appendChild(this._overflowMenuButton);
  }

  /**
   * overflowMenu を追加
   * @private
   */
  _addOverflowMenu() {
    this._overflowMenu = document.createElement('div');
    this._overflowMenu.classList.add(
      'shaka-settings-menu',
      'shaka-no-propagation',
      'shaka-show-controls-on-mouse-over',
      'shaka-hidden'
    );
    this._controlsContainer.appendChild(this._overflowMenu);

    // 各視点のボタンを追加
    [...Array(2)].forEach((_, i) => {
      const buttonContainer = document.createElement('div');
      buttonContainer.classList.add('view-points-button-container');
      [...Array(2)].forEach((_, j) => {
        const index = i * 2 + j;
        const viewPointButton = document.createElement('button');
        viewPointButton.classList.add('view-point-button');
        const viewPointName = this._playSettings[`view_point_${index + 1}`];
        viewPointButton.textContent = viewPointName ? viewPointName : "";
        viewPointButton.addEventListener('click', () => {
          this._changeViewPoint(index);
        })
        buttonContainer.appendChild(viewPointButton);
      })
      this._overflowMenu.appendChild(buttonContainer);
    })
  }

  /**
   * subViewContainer を追加
   * @private
   */
  _addSubViewContainer() {
    this._subViewContainerWrap = document.createElement('div');
    this._subViewContainerWrap.classList.add('sub-view-container-wrap');
    this._controlsContainer.appendChild(this._subViewContainerWrap);
    this._subViewContainer = document.createElement('div');
    this._subViewContainer.classList.add('sub-view-container', 'shaka-show-controls-on-mouse-over');
    this._subViewContainerWrap.appendChild(this._subViewContainer);

    // 各サブビューを canvas 要素へ描画
    [...Array(3)].forEach((_, i) => {
      const subView = document.createElement('canvas');
      subView.classList.add('sub-view', 'shaka-no-propagation');
      subView.addEventListener('click', () => {
        const currentSubViews = this._getCurrentSubViewLocations(this.currentViewPoint);
        this._changeViewPoint(currentSubViews[i]);
      })
      this._subViewContainer.appendChild(subView);
    })
  }

  /**
   * overflowMenuButton がクリックされた時の動作
   * @private
   */
  _onOverflowMenuButtonClick() {
    this._changeViewPoint();
    if (this.controls.anySettingsMenusAreOpen()) {
      this.controls.hideSettingsMenus();
    } else {
      this._overflowMenu.classList.remove('shaka-hidden');
    }
  }

  /**
   * メイン画面の視点変更
   * @private
   * @params {number} location - 切り替えする視点の番号（0〜3 | undefined）
   */
  _changeViewPoint(location) {
    const videoWidth = this._video.videoWidth;
    const videoHeight = this._video.videoHeight;
    const clientWidth = this._video.clientWidth;
    const clientHeight = this._video.clientHeight;
    const ratioX = clientWidth / videoWidth;
    const ratioY = clientHeight / videoHeight;
    const offsetWidth = (clientWidth - (ratioY * videoWidth)) / 2;
    const offsetHeight = (clientHeight - (ratioX * videoHeight)) / 2;
    const offsetX = Math.min((0.5 - (offsetWidth / clientWidth)), 0.5) * 100;
    const offsetY = Math.min((0.5 - (offsetHeight / clientHeight)), 0.5) * 100;

    let path = 'inset(0 0 0 0)';
    let transform = 'scale(1)';
    switch (location) {
      case 0:
        // TODO: 50.2% はアストロのコンテンツ向けの調整
        // 別案件で４視点再生を行う場合は調整が必要な可能性があるため留意
        path = 'inset(0 50% 50.2% 0)';
        transform = `translate(${offsetX}%, ${offsetY}%) scale(2)`;
        break;
      case 1:
        path = 'inset(0 0 50.2% 50%)';
        transform = `translate(-${offsetX}%, ${offsetY}%) scale(2)`;
        break;
      case 2:
        path = 'inset(50% 50% 0 0)';
        transform = `translate(${offsetX}%, -${offsetY}%) scale(2)`;
        break;
      case 3:
        path = 'inset(50% 0 0 50%)';
        transform = `translate(-${offsetX}%, -${offsetY}%) scale(2)`;
        break;
      default:
        break;
    }
    this._video.style.clipPath = path;
    this._video.style.transform = transform;
    this.currentViewPoint = location;
    this._updateSubViews(location);
  }

  /**
   * サブ画面の描画
   * @private
   * @params {number} location - 切り替えする視点の番号（0〜3 | undefined）
   */
  _updateSubViews(location) {
    if (this._subViewContainer) {
      const canvases = this._subViewContainer.children;
      const width = this._video.videoWidth / 2;
      const height = this._video.videoHeight / 2;

      let rowColumn = [[0, 0], [1, 0], [0, 1], [1, 1]];
      if (typeof location === 'undefined') {
        rowColumn.pop();
      } else {
        rowColumn.splice(location, 1);
      }
      Array.prototype.forEach.call(canvases, (canvas, i) => {
        const context = canvas.getContext('2d');
        const ratioY = canvas.height / height;
        const offsetWidth = (canvas.width - (ratioY * width)) / 2;
        context.drawImage(this._videoElement, rowColumn[i][0] * width, rowColumn[i][1] * height, width, height, offsetWidth, 0, ratioY * width, ratioY * height);
      })
    }
  }

  /**
   * location から現在表示されているサブビューの location 取得する
   * @private
   * @params {number} location - 視点の番号（0〜3 | undefined）
   * @return {number[]} subViewLocations - サブビューに表示されている location
   */
  _getCurrentSubViewLocations(location) {
    let subViewLocations = [0, 1, 2];
    switch (location) {
      case 0:
        subViewLocations = [1, 2, 3];
        break;
      case 1:
        subViewLocations = [0, 2, 3];
        break;
      case 2:
        subViewLocations = [0, 1, 3];
        break;
      case 3:
        break;
      default:
        break;
    }
    return subViewLocations;
  };

  /**
   * src のストリーミングプロトコルを返す
   * @private
   * @param {string} src - マニフェスト URL
   * @return {string} src のプロトコルが MPEG-DASH なら 'dash'、HLS なら 'hls'、どちらでもなければ 'unknown'
   */
  _getStreamingProtocol(src) {
    const ext = src.split('.').pop()
    switch (ext) {
      case 'mpd':
        return 'dash'
      case 'm3u8':
        return 'hls'
      default:
        return 'unknown'
    }
  }
}

MultiViewPointsMenu.Factory = class {
  constructor(playSettings, drm, src, player) {
    this.playSettings = playSettings;
    this.drm = drm;
    this.src = src;
    this.player = player;
  }

  create(rootElement, controls) {
    return new MultiViewPointsMenu(rootElement, controls, this.playSettings, this.drm, this.src, this.player);
  }
}

export default MultiViewPointsMenu;
